/**
 * Working around the Mapbox Directions API's 25-coordinate ceiling.
 *
 * That limit is per-request and applies to every account tier, so a scenic
 * route pinned down with 60 waypoints has to be split. Consecutive chunks share
 * their boundary waypoint, which is what lets the returned geometries be
 * concatenated into one continuous line with no gap and no duplicated vertex.
 *
 * This is in `core` rather than the planner because the same stitching has to
 * happen identically wherever a route gets rebuilt, and a seam bug here would
 * surface as a mysterious hairpin in the middle of a motorway.
 */

import type { LngLat } from './types.js';

/** Hard API limit. Not a tier thing — paid accounts get the same 25. */
export const MAPBOX_MAX_COORDS_PER_REQUEST = 25;

/**
 * Split into runs of at most `max`, each sharing one waypoint with the next.
 *
 * With max=25 every chunk after the first contributes 24 new waypoints, so the
 * request count is ceil((n - 1) / (max - 1)).
 */
export function chunkWaypoints<T>(waypoints: T[], max = MAPBOX_MAX_COORDS_PER_REQUEST): T[][] {
  if (max < 2) throw new RangeError('max must be at least 2');
  if (waypoints.length === 0) return [];
  if (waypoints.length === 1) return [waypoints.slice()];
  if (waypoints.length <= max) return [waypoints.slice()];

  const chunks: T[][] = [];
  let start = 0;
  while (start < waypoints.length - 1) {
    const end = Math.min(start + max - 1, waypoints.length - 1);
    chunks.push(waypoints.slice(start, end + 1));
    start = end;
  }
  return chunks;
}

export function expectedRequestCount(waypointCount: number, max = MAPBOX_MAX_COORDS_PER_REQUEST): number {
  if (waypointCount <= 1) return waypointCount === 1 ? 1 : 0;
  if (waypointCount <= max) return 1;
  return Math.ceil((waypointCount - 1) / (max - 1));
}

/**
 * Join per-chunk geometries into one path.
 *
 * Each chunk begins at the waypoint the previous chunk ended on, so its first
 * coordinate is a duplicate and gets dropped. Mapbox may return a coordinate a
 * few centimetres off the previous chunk's last one (independent snapping), so
 * we drop by position rather than by comparing values.
 */
export function stitchGeometries(chunks: LngLat[][]): LngLat[] {
  const out: LngLat[] = [];
  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i]!;
    if (chunk.length === 0) continue;
    if (out.length === 0) {
      out.push(...chunk);
    } else {
      out.push(...chunk.slice(1));
    }
  }
  return out;
}

/** Totals across chunks. Trivial, but easy to get wrong by double-counting seams. */
export function sumChunkTotals(chunks: Array<{ distance: number; duration: number }>): {
  distance: number;
  duration: number;
} {
  let distance = 0;
  let duration = 0;
  for (const c of chunks) {
    distance += c.distance;
    duration += c.duration;
  }
  return { distance, duration };
}
